import React from 'react';
import Button, { buttonSizes, buttonTypes } from '../Button';
import SvgDownArrowIcon from '../../svg/DownArrowIcon';
import SvgUpArrowIcon from '../../svg/UpArrowIcon';
import { Field } from 'react-final-form';
import SvgXIcon from '../../svg/XIcon';

export const selectorTypes = {
  radio: 'radio',
  checkbox: 'checkbox',
};

/**
 * Table Row component
 * @param {boolean} isTableHead - Determines if row is the header for a table
 * @param {boolean} hasSelector - Determines ia a selector column will appear at the left side of the row
 * @param {string} selectorType - Determines if the selector column will contain a checkbox or radio button
 * @param {object} data - The data to be displayed in the row
 * @param {string} headerStyles - CSS styles that will be added to each header cell
 * @param {string} tableSortParam - The parameter that the columns will be sorted by
 * @param {boolean} isChecked - Determines if the selector is checked
 * @param {string} selectorName - The name to put on the selector
 * @param {number} index - The number of the index for this row
 */

function TableRow(props) {
  const {
    data: rowData,
    isTableHead,
    tableSortOrder,
    tableSortParam,
    setTableHeadSort,
    hasSelector,
    index: rowIndex,
    selectorName,
    selectorType,
  } = props;
  /**
   * Function to generate the row contents
   * @returns {array} - The divs making up each table row
   */
  const rowContents = () => {
    // Take in the object of data for the row and convert to an array
    const data = Object.values(rowData);
    // Iterate over each item
    const contents = data.map((cell, index) => {
      const cellStyle = cell.style ? cell.style : '';
      // Determine if the item is for a table heading row
      if (isTableHead) {
        let tableHeadContents;
        // If the column of information is going to be sortable add the arrow icon for the button in the heading row based on the sort order
        if (cell.isSortable) {
          let rightIcon;
          if (tableSortParam === cell.sortBy) {
            if (tableSortOrder === 'az') {
              rightIcon = <SvgUpArrowIcon />;
            } else {
              rightIcon = <SvgDownArrowIcon />;
            }
          }
          // Add the button for sorting to the tableHeadContents variable for display
          tableHeadContents = (
            <Button
              onClick={() => setTableHeadSort(cell.sortBy)}
              text={cell.display}
              rightIcon={rightIcon}
              buttonType={buttonTypes.noButton}
              buttonSize={buttonSizes.small}
              value={cell.sortBy}
              data-test="sort-button"
              buttonStyle={
                'text-medium ' +
                (tableSortParam === cell.sortBy
                  ? 'no-margin text-extra-bold text-very-light-grey'
                  : 'no-margin text-regular text-white')
              }
            />
          );
        } else {
          // If the column is not going to be sortable, just display the text for the column heading
          tableHeadContents = cell.display;
        }
        // Return the heading cell
        return (
          <div
            data-test="column-header"
            role="columnheader"
            key={index}
            className={`table__header text-bold table__col${index} ${cellStyle}`}>
            {tableHeadContents}
          </div>
        );
      } else {
        // If the data is not for a heading row, return the data as a normal table cell
        if (hasSelector && index === 0) {
          return (
            <div
              data-test="table-cell-selector"
              role="cell"
              key={index}
              className={
                rowIndex % 2
                  ? `table__row table__col${index} ${
                      cell.style ? cellStyle : 'table__row--alternate'
                    }`
                  : `table__row table__col${index} ${cellStyle}`
              }>
              <label>
                <SvgXIcon className="rotate-45-cw" />
                <Field
                  name={selectorName}
                  component="input"
                  type={selectorType || selectorTypes.checkbox}
                  value={cell.display}
                  className="visually-hidden"
                  data-test="table-row-selector"
                />
              </label>
            </div>
          );
        } else {
          return (
            <div
              data-test="table-cell"
              role="cell"
              key={index}
              className={
                rowIndex % 2
                  ? `table__row table__col${index} ${
                      cell.style ? cellStyle : 'table__row--alternate'
                    }`
                  : `table__row table__col${index} ${cellStyle}`
              }>
              {cell.display}
            </div>
          );
        }
      }
    });
    // Return the row of information

    return contents;
  };

  return <React.Fragment>{rowContents()}</React.Fragment>;
}
export default TableRow;
